import { useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { requestMagicLink } from "../api/auth.js";
import { ApiCallError } from "../api/client.js";
import { Button } from "../components/button.js";
import { FormField } from "../components/form-field.js";
import { useSession } from "../hooks/use-session.js";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ViewState = "form" | "sent";
type Mode = "signin" | "signup";

export function SignInPage() {
  const { isAuthenticated, isLoading: sessionLoading } = useSession();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  // Redirect already-authenticated users to app
  if (!sessionLoading && isAuthenticated) {
    return <Navigate to={returnTo || "/app"} replace />;
  }

  const [email, setEmail] = useState("");
  const [view, setView] = useState<ViewState>("form");
  const [mode, setMode] = useState<Mode>("signin");
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function handleModeChange(newMode: Mode) {
    setMode(newMode);
    setError(null);
    setEmailError(null);
    setEmail("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailError(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Email is required");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError("Enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      // Preserve return URL for after magic link verification
      if (returnTo) {
        sessionStorage.setItem("auth_return_to", returnTo);
      }
      await requestMagicLink(email.trim());
      setView("sent");
    } catch (err) {
      setError(
        err instanceof ApiCallError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-card__logo">
          Accessi<span>Monitor</span>
        </div>

        {view === "sent" ? (
          <>
            <h1 className="auth-card__title">Check your email</h1>
            <p className="auth-card__subtitle">
              We sent a sign-in link to <strong>{email}</strong>. It expires in
              15 minutes.
            </p>
            <p className="text-sm text-muted" style={{ marginTop: "0.75rem" }}>
              Check your spam or junk folder if you do not see it.
            </p>
            <p className="text-sm text-muted" style={{ marginTop: "0.5rem" }}>
              Did not receive it?{" "}
              <button
                className="btn-link"
                onClick={() => setView("form")}
              >
                Try again
              </button>
            </p>
          </>
        ) : (
          <>
            <div className="auth-tabs">
              <button
                className={`auth-tab ${mode === "signin" ? "auth-tab--active" : ""}`}
                onClick={() => handleModeChange("signin")}
              >
                Sign in
              </button>
              <button
                className={`auth-tab ${mode === "signup" ? "auth-tab--active" : ""}`}
                onClick={() => handleModeChange("signup")}
              >
                Sign up
              </button>
            </div>

            <h1 className="auth-card__title">
              {mode === "signin" ? "Sign in" : "Create account"}
            </h1>
            <p className="auth-card__subtitle">
              {mode === "signin"
                ? "Enter your email and we will send you a sign-in link."
                : "Enter your email to create a new account."}
            </p>

            {error && (
              <div className="alert alert--error" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <FormField id="email" label="Email address" required error={emailError ?? undefined}>
                <input
                  id="email"
                  type="email"
                  className="form-field__input"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  autoComplete={mode === "signin" ? "email" : "off"}
                  placeholder="you@example.com"
                  required
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? "email-error" : undefined}
                />
              </FormField>
              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!email.trim()}
              >
                {mode === "signin" ? "Send sign-in link" : "Create account"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
